import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';

interface Props {
  readonly open: boolean;
  readonly busy: boolean;
  /** Called with the moment to publish, as ISO UTC. */
  readonly onSchedule: (publishAt: string) => void;
  readonly onCancel: () => void;
}

/** A local date and time as the value a datetime-local input takes. */
const localInputValue = (date: Date): string => {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Picks when a draft goes live. In the administrator's own time: that is the clock they are
 * thinking in when they say "Monday at seven", and the server is told the instant, in UTC.
 */
const SchedulePublishDialog = ({
  open, busy, onSchedule, onCancel,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  useEffect(() => {
    if (open) {
      const inAnHour = new Date(Date.now() + (60 * 60 * 1000));
      inAnHour.setMinutes(0, 0, 0);
      setValue(localInputValue(inAnHour));
    }
  }, [open]);

  const chosen = value ? new Date(value) : undefined;
  const valid = chosen !== undefined && !Number.isNaN(chosen.getTime()) && chosen.getTime() > Date.now();

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{t('floorplan-editor-schedule-title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">{t('floorplan-editor-schedule-hint')}</Typography>
          <TextField
            type="datetime-local"
            size="small"
            label={String(t('floorplan-editor-schedule-when'))}
            value={value}
            onChange={(event): void => setValue(event.target.value)}
            inputProps={{ min: localInputValue(new Date()), 'aria-label': String(t('floorplan-editor-schedule-when')) }}
            error={!!value && !valid}
            helperText={value && !valid ? String(t('floorplan-editor-schedule-in-future')) : ' '}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || busy}
          onClick={(): void => {
            if (chosen && valid) onSchedule(chosen.toISOString());
          }}
        >
          {t('floorplan-editor-schedule-confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SchedulePublishDialog;
